import React, { useState, useRef, useEffect } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Webcam from "react-webcam"
import Spinner from "../common/Spinner"
import FlashOverlay from "../common/FlashOverlay"
import CountdownView from "../common/CountdownView"
import IconButton from "../buttons/IconButton"
import { ReactComponent as CameraIcon } from "../../icon/camera.svg"
import PermissionModal from "../modals/PermissionModal"
import { CSSTransition } from "react-transition-group"
import { useTranslation } from "react-i18next"
import FaceGuide from "../../image/face-guide.png"
import shutterSound from "../../sound/camera-shutter.mp3"
import beepSound from "../../sound/beep.mp3"
import { cropImage } from "../../utils/ImageUtil"
import { useCameraPermission } from "../../hooks/useCameraPermission"
import MediaContainer from "../common/MediaContainer"
import { useConfig } from "../../hooks/useConfig"
import { aspectRatioToNumeric } from "../../utils/AspectRatioUtil"
import { isMobileOrTablet } from "../../utils/DeviceUtil"

interface CameraViewProps {
  theme: any
  startCapture: boolean
  selectedCamera: MediaDeviceInfo | null
  showFaceGuide?: boolean
  showCameraButton: boolean
  onStartCapture: () => void
  postDelay: number
  handleDevices: (devices: MediaDeviceInfo[]) => void
  finishCountdown: () => void
  handleImage: (image: string, source: string) => void
  handleError: (error: string) => void
}

function CameraView({
  theme,
  startCapture,
  selectedCamera,
  showFaceGuide,
  showCameraButton,
  onStartCapture,
  postDelay,
  handleDevices,
  finishCountdown,
  handleImage,
  handleError,
}: CameraViewProps) {
  const { t } = useTranslation()
  const [timerValue, setTimerValue] = useState(-1)
  const [showModal, setShowModal] = useState(false)
  const [mediaReceived, setMediaReceived] = useState(false)
  const [sequenceStarted, setSequenceStarted] = useState(false)
  const webcam = useRef<any>(null)
  const canvas = useRef<HTMLCanvasElement | null>(null)

  const config = useConfig()
  const { isGranted, isLoading } = useCameraPermission()
  const [showRotateHint, setShowRotateHint] = useState(false)

  const receiveFirstMedia = () => {
    setMediaReceived(true)
    setShowModal(false)

    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      let videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput")
      handleDevices(videoDevices)
    })
  }

  // Show permission modal after delay, but only if permission isn't already granted
  useEffect(() => {
    if (isLoading || isGranted || mediaReceived) return

    const timeoutId = setTimeout(() => {
      if (!mediaReceived) {
        setShowModal(true)
      }
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [isLoading, isGranted, mediaReceived])

  const startSequence = () => {
    setTimerValue(3)
    tick(3)
  }

  const tick = (currentValue: number) => {
    if (currentValue >= 1) {
      setTimeout(() => {
        let newValue = currentValue - 1
        setTimerValue(newValue)
        if (newValue > 0) {
          tick(newValue)
        }
        if (newValue === 0) {
          finishCountdown()
          setTimeout(() => {
            captureImage()
          }, 200)
        }
      }, 1000)
    }
  }

  const captureImage = () => {
    if (!webcam || !webcam.current) {
      return
    }

    webcam.current.video?.pause()

    let imageData = webcam.current.getScreenshot()
    let image = new Image()
    image.src = imageData
    image.onload = function () {
      const targetAr = aspectRatioToNumeric(config.captureAspectRatio)
      const sourceAr = image.naturalWidth / image.naturalHeight

      let cropPixels
      if (sourceAr > targetAr) {
        // Source is wider than target — crop sides
        const cropWidth = Math.round(image.naturalHeight * targetAr)
        cropPixels = {
          x: Math.round((image.naturalWidth - cropWidth) / 2),
          y: 0,
          width: cropWidth,
          height: image.naturalHeight,
        }
      } else {
        // Source is taller than target — crop top/bottom
        const cropHeight = Math.round(image.naturalWidth / targetAr)
        cropPixels = {
          x: 0,
          y: Math.round((image.naturalHeight - cropHeight) / 2),
          width: image.naturalWidth,
          height: cropHeight,
        }
      }

      if (!canvas.current) {
        throw new Error("Canvas missing")
      }

      imageData = cropImage(image, canvas.current, cropPixels, mirrored, config.captureAspectRatio)

      setTimeout(() => {
        handleImage(imageData, "camera")
      }, postDelay)
    }
  }

  // Start countdown
  if (startCapture && timerValue === -1 && !sequenceStarted) {
    setSequenceStarted(true)
    setTimeout(startSequence, 800)
  }

  const numericAspect = aspectRatioToNumeric(config.captureAspectRatio)

  // Check orientation mismatch on mobile and show rotation hint
  useEffect(() => {
    if (!isMobileOrTablet() || config.captureAspectRatio === "1:1") return

    const portraitQuery = window.matchMedia("(orientation: portrait)")
    const checkOrientation = () => {
      const isPortraitOrientation = portraitQuery.matches
      const isPortraitAspect = numericAspect < 1
      const mismatch = isPortraitAspect !== isPortraitOrientation
      setShowRotateHint(mismatch)
    }

    checkOrientation()
    portraitQuery.addEventListener("change", checkOrientation)
    return () => portraitQuery.removeEventListener("change", checkOrientation)
  }, [config.captureAspectRatio, numericAspect])

  let videoConstraints: any = {
    aspectRatio: numericAspect,
    width: 1920,
    height: 1080,
  }

  let mirrored = true
  if (selectedCamera) {
    videoConstraints.deviceId = selectedCamera.deviceId
    if (selectedCamera.label.toLowerCase().includes("back")) {
      mirrored = false
    }
  }

  // Play beep sound on each countdown tick
  useEffect(() => {
    if (timerValue > 0) {
      const audio = new Audio(beepSound)
      audio.volume = 0.5
      audio.play()
    }
  }, [timerValue])

  // Play shutter sound when capture completes
  useEffect(() => {
    if (timerValue === 0) {
      const audio = new Audio(shutterSound)
      audio.play()
    }
  }, [timerValue])

  const imgFill = { display: "block", position: "absolute", inset: 0, width: "100%" } as const
  const overlaySx = {
    position: "relative",
    inset: 0,
    backgroundSize: "100% 100%",
    zIndex: 3,
    pointerEvents: "none",
  } as const

  return (
    <>
      <canvas
        className="hiddenCanvas"
        ref={canvas}
      ></canvas>

      <FlashOverlay show={timerValue === 0} />

      <CSSTransition
        in
        appear
        timeout={500}
        exit={false}
        classNames="camera"
        unmountOnExit
      >
        <MediaContainer
          sx={{
            userSelect: "none",
            WebkitTouchCallout: "none",
            mx: "auto",
          }}
        >
          <CountdownView value={timerValue} />

          <CSSTransition
            in={theme.firstOverlayUrl && mediaReceived}
            appear
            timeout={500}
            classNames="fade"
            unmountOnExit
          >
            <Box sx={overlaySx}>
              <Box
                component="img"
                crossOrigin="anonymous"
                src={theme.firstOverlayUrl}
                alt="Overlay"
                sx={imgFill}
              />
            </Box>
          </CSSTransition>

          <CSSTransition
            in={showFaceGuide && config.captureAspectRatio === "1:1"}
            appear
            timeout={500}
            classNames="fade"
            unmountOnExit
          >
            <Box sx={overlaySx}>
              <Box
                component="img"
                crossOrigin="anonymous"
                src={FaceGuide}
                alt="Guide"
                sx={imgFill}
              />
            </Box>
          </CSSTransition>


          <Box sx={{ position: "absolute", inset: 0, zIndex: 1 }}>
            <Webcam
              ref={webcam}
              audio={false}
              mirrored={mirrored}
              imageSmoothing
              forceScreenshotSourceSize
              videoConstraints={videoConstraints}
              onUserMedia={receiveFirstMedia}
              onUserMediaError={(exception) => {
                handleError(typeof exception === "string" ? exception : exception.message)
              }}
            />
          </Box>

          {timerValue === -1 && (
            <Spinner
              color={theme.textStyle.color}
              centerVertical
            />
          )}

          {showRotateHint && (
            <Typography
              sx={{
                position: "absolute",
                top: 16,
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 4,
                ...theme.textStyle,
                fontSize: "1.1rem",
                bgcolor: "rgba(0,0,0,0.5)",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                mx: "auto",
                width: "fit-content",
              }}
            >
              {t("camera.rotateDevice")}
            </Typography>
          )}
        </MediaContainer>
      </CSSTransition>

      <CSSTransition
        in={showCameraButton}
        appear
        timeout={300}
        classNames="iconAnimate"
        unmountOnExit
      >
        <Box sx={{ textAlign: "center", mt: "-54px", position: "relative", zIndex: 100 }}>
          <IconButton
            theme={theme}
            onPress={onStartCapture}
          >
            <CameraIcon style={{ ...theme.svgStyle, width: 48, height: 48 }} />
          </IconButton>
        </Box>
      </CSSTransition>

      <PermissionModal show={showModal} />
    </>
  )
}

export default CameraView
